import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';
import { User } from 'react-feather';
import { addMonths } from 'date-fns';
import { memo } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import groupBy from 'lodash/groupBy';

import { GetDashboardStatsQuery as GetDashboardStats } from '__generated__/graphql';

import { FilterIndicator } from './Layout';
import { Theme } from '../../theme';
import { assetUrl } from '../../common/helpers';
import { months } from '../../config';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';

type RegistrationCounts = GetDashboardStats['project']['analytics']['monthly_registration_counts'][0];

interface DataPoint {
  x: number;
  [year: number]: number;
}

interface Year {
  year: number;
  title: string;
  color: string;
}

interface TotalRegistrationsGraphProps {
  data: RegistrationCounts[];
}

const TotalRegistrationsGraph = memo(({ data }: TotalRegistrationsGraphProps) => {
  const { t } = useTranslation();
  const theme = useTheme() as Theme;

  const formatXTick = (month: number) => t(`common:months.${months[month - 1]}`);

  const graphData: DataPoint[] = [];
  const uniqueYears: number[] = [];
  const monthlyData = groupBy(data, 'month');
  const xMin = 1;
  const xMax = 12;
  const yMax = data.reduce((max, { count }) => Math.max(max, count), 0);

  // Convert the registration counts to an array that looks as follows:
  // [{ x: 1, 2024: 78, 2025: 55 }]
  for (let x = xMin; x <= xMax; x++) {
    graphData.push({
      x,
      ...monthlyData[x]?.reduce((data, { year, count }) => {
        if (!uniqueYears.includes(year)) {
          uniqueYears.push(year);
        }

        return {
          ...data,
          [year]: count,
        };
      }, {}),
    });
  }

  const years: Year[] = uniqueYears.sort()
    .slice(uniqueYears.length - 2) // Show the last 2 years.
    .map((year, index) => ({
      title: `${year}`,
      year,
      color: uniqueYears.length === 1 || index === 1 ? theme.colors.primary[500] : theme.colors.gray[400],
    }));

  return (
    <UI.Card>
      <UI.FlexContainer sc={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <UI.FlexContainer sc={{ alignItems: 'center' }}>
          <UI.H4>
            {t('registrations_per_month')}
          </UI.H4>
          <FilterIndicator sc={{ ml: 2 }} alignV="bottom" />
        </UI.FlexContainer>
        <UI.FlexContainer sc={{ alignItems: 'center' }}>
          {years.map(({ title, year, color }, index) => (
            <UI.FlexContainer sc={{ alignItems: 'center', ml: 3 }} key={year}>
              <UI.Span
                sc={{ fontWeight: 500, small: true, pr: index === 0 ? 1 : 0, inlineBlock: true }}
                style={{ color, maxWidth: years.length >= 3 ? 200 : 300 }}
              >
                <UI.Truncate>
                  {title}
                </UI.Truncate>
              </UI.Span>
            </UI.FlexContainer>
          ))}
        </UI.FlexContainer>
      </UI.FlexContainer>

      <UI.Div style={{ height: 300 }}>
        {graphData.length > 0 && (
          <ResponsiveContainer>
            <BarChart
              height={300}
              data={graphData}
              margin={{ top: 10, right: 5, bottom: 10, left: 0 }}
            >
              <CartesianGrid
                stroke={theme.colors.gray[100]}
                vertical={false}
              />
              <XAxis
                dataKey="x"
                type="category"
                domain={[xMin, xMax]}
                tickFormatter={formatXTick}
                tick={{ fontSize: 10 }}
                stroke={theme.colors.gray[500]}
                dy={10}
              />
              <YAxis
                strokeWidth={0}
                tick={{ fontSize: 11 }}
                width={`${yMax}`.length * 6.4 + 18}
                allowDecimals={false}
              />
              <Tooltip
                cursor={{ fill: theme.transparentize(theme.colors.gray[100], 0.5) }}
                content={(props: TooltipProps<number, number>) => (
                  <CustomTooltip years={years} content={props} />
                )}
              />
              {years.map((plot, index) => (
                <Bar
                  type="monotone"
                  dataKey={plot.year}
                  fill={plot.color}
                  opacity={years.length > 1 && index < years.length - 1 ? 0.3 : 1}
                  key={index}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
        {graphData.length === 0 && (
          <UI.FlexContainer
            sc={{ justifyContent: 'center', alignItems: 'center', direction: 'column' }}
            style={{ height: '100%' }}
          >
            <img
              src={assetUrl('images/dashboard-registrations.svg')}
              height={100}
              alt={t('registrations')}
              style={{ display: 'block' }}
            />
            <UI.Div sc={{ textAlign: 'center', color: 'gray.400', mt: 4 }}>
              {t('no_data_available')}
            </UI.Div>
          </UI.FlexContainer>
        )}
      </UI.Div>
    </UI.Card>
  );
});

interface CustomTooltipProps {
  years: Year[];
  content: TooltipProps<number, number>;
}

const CustomTooltip = ({ years, content: { active, payload, label: month } }: CustomTooltipProps) => {
  const { formatInteger, formatDate } = useLocale();

  const formatMonth = (year: number, month: number) => formatDate(
    addMonths(new Date(year, 0, 1), month - 1),
    { format: 'display_month' },
  );

  return active && payload.length > 0 && (
    <UI.Card sc={{ padding: 2, active: true, small: true }} style={{ borderRadius: 8 }}>
      <UI.GridContainer sc={{ gutter: 0.25 }}>
        {payload.map((payload, index) => {
          const { title, color } = (years).find((plot) => plot.year === payload.name);

          return (
            <UI.Div key={index}>
              <UI.Strong sc={{ block: true }} style={{ color }}>
                {title}
              </UI.Strong>
              <UI.Delimit>
                <>
                  <UI.Icon>
                    <User />
                  </UI.Icon>
                  {' '}
                  {formatInteger(payload.value)}
                </>
                {formatMonth(payload.name, month)}
              </UI.Delimit>
            </UI.Div>
          );
        })}
      </UI.GridContainer>
    </UI.Card>
  );
};

export default TotalRegistrationsGraph;
